import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import type { ReactElement } from "react";
import { LoginPage } from "./pages/LoginPage";
import { DiaryListPage } from "./pages/DiaryListPage";
import { DiaryFormPage } from "./pages/DiaryFormPage";
import { RegisterPage } from "./pages/RegisterPage";
import { AppHeader } from "./components/AppHeader";
import { AppFooter } from "./components/AppFooter";
import { LanguageProvider } from "./i18n/LanguageContext";
import type { Language } from "./i18n/translations";
import { getSettings, updateSettings } from "./api/settingsApi";
import "./App.css";

function isTokenValid(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

/** Reads navigator.language and maps it to a supported language, defaulting to "en". */
function detectBrowserLanguage(): Language {
  const lang = (navigator.language ?? "").toLowerCase();
  return lang.startsWith("de") ? "de" : "en";
}

function ProtectedRoute({
  children,
}: {
  children: ReactElement;
}): ReactElement {
  const token = localStorage.getItem("token");

  if (!token || !isTokenValid(token)) {
    localStorage.removeItem("token");
    return <Navigate to="/login" replace />;
  }

  return children;
}

interface LayoutProps {
  children: ReactElement;
  darkMode: boolean;
  onToggleDark: () => void;
}

function ProtectedLayout({
  children,
  darkMode,
  onToggleDark,
}: LayoutProps): ReactElement {
  return (
    <>
      <AppHeader darkMode={darkMode} onToggleDark={onToggleDark} />
      {children}
    </>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const cached = localStorage.getItem("darkMode");
    if (cached !== null) return cached === "true";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const [language, setLanguageState] = useState<Language>(() => {
    // Use explicit cached preference; otherwise follow the browser.
    const cached = localStorage.getItem("language") as Language | null;
    if (cached === "en" || cached === "de") return cached;
    return detectBrowserLanguage();
  });

  // Load user settings from backend once on mount (if authenticated)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !isTokenValid(token)) return;

    getSettings()
      .then((s) => {
        if (s.theme === "light" || s.theme === "dark") {
          setDarkMode(s.theme === "dark");
        }
        if (s.language) {
          setLanguageState(s.language);
          localStorage.setItem("language", s.language);
        } else {
          // null → no explicit preference, follow the browser
          localStorage.removeItem("language");
          setLanguageState(detectBrowserLanguage());
        }
      })
      .catch(() => {
        // Non-critical — keep locally cached state
      });
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute(
      "data-theme",
      darkMode ? "dark" : "light",
    );
    localStorage.setItem("darkMode", String(darkMode));
  }, [darkMode]);

  async function toggleDark() {
    const next = !darkMode;
    setDarkMode(next);
    const token = localStorage.getItem("token");
    if (token && isTokenValid(token)) {
      updateSettings({ theme: next ? "dark" : "light" }).catch(() => {});
    }
  }

  async function handleSetLanguage(lang: Language) {
    setLanguageState(lang);
    localStorage.setItem("language", lang);
    const token = localStorage.getItem("token");
    if (token && isTokenValid(token)) {
      updateSettings({ language: lang }).catch(() => {});
    }
  }

  return (
    <LanguageProvider language={language} onSetLanguage={handleSetLanguage}>
      <BrowserRouter>
        <div className="app-shell">
          <div className="app-content">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              <Route
                path="/diaries"
                element={
                  <ProtectedRoute>
                    <ProtectedLayout
                      darkMode={darkMode}
                      onToggleDark={toggleDark}
                    >
                      <DiaryListPage />
                    </ProtectedLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/diaries/new"
                element={
                  <ProtectedRoute>
                    <ProtectedLayout
                      darkMode={darkMode}
                      onToggleDark={toggleDark}
                    >
                      <DiaryFormPage />
                    </ProtectedLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/diaries/:id"
                element={
                  <ProtectedRoute>
                    <ProtectedLayout
                      darkMode={darkMode}
                      onToggleDark={toggleDark}
                    >
                      <DiaryFormPage />
                    </ProtectedLayout>
                  </ProtectedRoute>
                }
              />

              <Route path="/" element={<Navigate to="/diaries" replace />} />
              <Route path="*" element={<Navigate to="/diaries" replace />} />
            </Routes>
          </div>
          <AppFooter />
        </div>
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
